'use client'

import { useState } from 'react'
import Link from 'next/link'
import {
  Layers,
  FileText,
  Link2,
  Fingerprint,
  Shield,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  Mail,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import ScoreCard from './ScoreCard'
import type { Finding } from './ScoreCard'
import SectionBreakdown from './SectionBreakdown'
import PlatformCTA from '@/components/platform/PlatformCTA'
import { sanitizeHref } from '@/lib/sanitize'

// Matches API response shape
interface LlmsValidatorResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  llmsTxtFound: boolean
  llmsFullTxtFound: boolean
  blocked: boolean
  rawContent: string
  rawFullContent: string
  wordCount: number
  fullWordCount: number
  sections: {
    heading: string
    type: string
    wordCount: number
    linkCount: number
    contentPreview: string
    fullContent: string
  }[]
  links: {
    text: string
    url: string
    isInternal: boolean
  }[]
  entityDefinitionCount: number
  scoreBreakdown: {
    category: string
    score: number
    maxScore: number
    details: string
    color: string
  }[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
  parsedMetadata: {
    h1Title: string | null
    hasBlockquote: boolean
    sectionCount: number
    internalLinkCount: number
    externalLinkCount: number
  }
}

interface ValidatorResultsProps {
  result: LlmsValidatorResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share widget, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score breakdown, sections, links, raw view, recs) is
   * identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const BLOG_LINKS: Record<string, string> = {
  'Create llms.txt': '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  Structure: '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  Content: '/blogs/build-brand-ai-search-engines-cite',
  Links: '/blogs/knowledge-graph-llm-visibility-real-data',
  'Entity Definitions': '/blogs/build-brand-ai-search-engines-cite',
  'Use Policy': '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  Completeness: '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const SCORE_ICONS: Record<string, React.ReactNode> = {
  Structure: <Layers size={18} />,
  'Content Sections': <FileText size={18} />,
  'Links & URLs': <Link2 size={18} />,
  'Entity Definitions': <Fingerprint size={18} />,
  'Use Policy': <Shield size={18} />,
  Completeness: <CheckCircle2 size={18} />,
}

export default function ValidatorResults({
  result,
  variant = 'public',
}: ValidatorResultsProps) {
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')
  const [showRawContent, setShowRawContent] = useState(false)

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/llms-validator/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          url: result.url,
          domain: result.domain,
          overallScore: result.overallScore,
          grade: result.grade,
          llmsTxtFound: result.llmsTxtFound,
          llmsFullTxtFound: result.llmsFullTxtFound,
          wordCount: result.wordCount,
          scoreBreakdown: result.scoreBreakdown,
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  // Build findings from score breakdown
  function buildFindings(category: string): Finding[] {
    const item = result.scoreBreakdown.find(s => s.category === category)
    if (!item) return []
    return item.details
      .split('. ')
      .filter(Boolean)
      .map(detail => ({
        text: detail,
        status:
          detail.toLowerCase().includes('found') ||
          detail.toLowerCase().includes('present') ||
          detail.toLowerCase().includes('good') ||
          detail.toLowerCase().includes('rich') ||
          detail.toLowerCase().includes('strong') ||
          detail.toLowerCase().includes('clear') ||
          detail.toLowerCase().includes('clean') ||
          detail.toLowerCase().includes('comprehensive') ||
          detail.toLowerCase().includes('bonus') ||
          detail.toLowerCase().includes('spread')
            ? ('good' as const)
            : detail.toLowerCase().includes('no ') ||
                detail.toLowerCase().includes('missing') ||
                detail.toLowerCase().includes('thin') ||
                detail.toLowerCase().includes('very short') ||
                detail.toLowerCase().includes('minimal') ||
                detail.toLowerCase().includes('only') ||
                detail.toLowerCase().includes('not')
              ? ('bad' as const)
              : ('neutral' as const),
      }))
  }

  const internalLinks = result.links.filter(l => l.isInternal)
  const externalLinks = result.links.filter(l => !l.isInternal)

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>Results for</p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.url}
            </p>
          </div>
          <OverallScore score={result.overallScore} grade={result.grade} />
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only */}
      {variant !== 'dashboard' && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='llms.txt'
          score={result.overallScore}
          grade={result.grade}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Quick Status Badges */}
      <LenisFadeIn>
        <div className='flex flex-wrap justify-center gap-2'>
          <span
            className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
            style={{
              backgroundColor: result.llmsTxtFound ? '#22c55e15' : '#F90B8A15',
              color: result.llmsTxtFound ? '#22c55e' : '#F90B8A',
            }}
          >
            {result.llmsTxtFound ? (
              <CircleCheck size={14} />
            ) : (
              <AlertTriangle size={14} />
            )}
            llms.txt {result.llmsTxtFound ? 'found' : 'not found'}
          </span>
          <span
            className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
            style={{
              backgroundColor: result.llmsFullTxtFound
                ? '#22c55e15'
                : '#64748b15',
              color: result.llmsFullTxtFound ? '#22c55e' : '#64748b',
            }}
          >
            llms-full.txt {result.llmsFullTxtFound ? 'found' : 'not found'}
          </span>
          {result.llmsTxtFound && (
            <>
              <span
                className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
                style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
              >
                {result.wordCount} words
              </span>
              <span
                className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
                style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}
              >
                {result.parsedMetadata.sectionCount} sections
              </span>
              <span
                className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
                style={{ backgroundColor: '#20808d15', color: '#20808d' }}
              >
                {result.links.length} links
              </span>
            </>
          )}
        </div>
      </LenisFadeIn>

      {/* Score Breakdown */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {result.scoreBreakdown.map(item => (
            <ScoreCard
              key={item.category}
              title={item.category}
              icon={SCORE_ICONS[item.category] || <FileText size={18} />}
              score={item.score}
              maxScore={item.maxScore}
              findings={buildFindings(item.category)}
              color={item.color}
            />
          ))}
        </div>
      </LenisStaggered>

      {/* Section Breakdown */}
      {result.llmsTxtFound && (
        <LenisFadeIn>
          <SectionBreakdown sections={result.sections} />
        </LenisFadeIn>
      )}

      {/* Links Found */}
      {result.links.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Link2 size={20} style={{ color: '#20808d' }} />
              Links Found
              <span className='text-xs font-normal text-muted-foreground ml-1'>
                ({result.links.length})
              </span>
            </h2>

            {internalLinks.length > 0 && (
              <div className='mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2'>
                  Internal ({internalLinks.length})
                </h3>
                <div className='flex flex-wrap gap-2'>
                  {internalLinks.map((link, i) => {
                    const safeUrl = sanitizeHref(link.url)
                    if (!safeUrl) return null
                    return (
                      <a
                        key={i}
                        href={safeUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 bg-background border border-border/60 text-foreground/80 hover:text-foreground hover:border-border transition-colors'
                      >
                        {link.text.length > 40
                          ? link.text.slice(0, 40) + '...'
                          : link.text}
                        <ExternalLink size={10} className='shrink-0' />
                      </a>
                    )
                  })}
                </div>
              </div>
            )}

            {externalLinks.length > 0 && (
              <div>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2'>
                  External ({externalLinks.length})
                </h3>
                <div className='flex flex-wrap gap-2'>
                  {externalLinks.map((link, i) => {
                    const safeUrl = sanitizeHref(link.url)
                    if (!safeUrl) return null
                    return (
                      <a
                        key={i}
                        href={safeUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 bg-background border border-border/60 text-foreground/80 hover:text-foreground hover:border-border transition-colors'
                      >
                        {link.text.length > 40
                          ? link.text.slice(0, 40) + '...'
                          : link.text}
                        <ExternalLink size={10} className='shrink-0' />
                      </a>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </LenisFadeIn>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Recommendations
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Raw Content */}
      {result.rawContent && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <button
              onClick={() => setShowRawContent(!showRawContent)}
              className='flex items-center justify-between w-full'
            >
              <h2 className='text-lg font-semibold text-foreground flex items-center gap-2'>
                <FileText size={20} style={{ color: '#64748b' }} />
                Raw llms.txt
              </h2>
              {showRawContent ? (
                <ChevronUp size={20} className='text-muted-foreground' />
              ) : (
                <ChevronDown size={20} className='text-muted-foreground' />
              )}
            </button>

            {showRawContent && (
              <pre className='mt-4 text-xs text-foreground/70 leading-relaxed whitespace-pre-wrap font-mono bg-background border border-border/60 rounded-xl p-4 max-h-96 overflow-auto'>
                {result.rawContent}
              </pre>
            )}
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #8c1b4c, #b32a6c, #e54b8c)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Need help building your llms.txt?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              We build knowledge graphs and llms.txt files that AI systems
              actually cite. Our implementation is dynamic, entity-aware, and
              backed by real results.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#b32a6c'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <Link
                href='/blogs/llms-txt-static-vs-dynamic-implementation-guide'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Read our llms.txt guide{' '}
                <ArrowRight size={16} className='ml-1' />
              </Link>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
